import { createReducer, on } from '@ngrx/store';
import * as PaymentActions from '@store/payment/payment.actions';
import { Payment, Transaction } from '@core/models';
import { Currency, InvoiceLink as InvoiceLinkApi, PaymentStatus, PaymentType } from '@api/backend';
import {
  FormGroupState,
  markAsDirty,
  markAsPristine,
  markAsTouched,
  markAsUntouched,
  onNgrxForms,
  onNgrxFormsAction,
  setValue,
  SetValueAction,
  StartAsyncValidationAction,
  wrapReducerWithFormStateUpdate,
} from 'ngrx-forms';
import {
  initialPaymentChargeFormState,
  PAYMENT_CHARGE_FORM_ID,
  PaymentChargeFormState,
  validatePaymentChargeForm,
} from './payment-charge.form';
import {
  initialPaymentWithdrawFormState,
  PAYMENT_WITHDRAW_FORM_ID,
  PaymentWithdrawFormState,
  validatePaymentWithdrawForm,
} from './payment-withdraw.form';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { UserTransactionsViewFilter } from '@store/user';
import { paymentActions } from '@store/payment/payment.actions';


export const paymentFeatureKey = 'payment';

export interface TransactionListState extends EntityState<Transaction> {
  nextPage?: number,
  currentView: UserTransactionsViewFilter,
  isLoading: boolean
}

export const transactionListAdapter = createEntityAdapter<Transaction>({
  selectId: transaction => transaction.sub_type + transaction.transaction_id,
});

export enum PaymentModes {
  Default = 'default',
  PaymentCode = 'payment_code',
  InvoiceLink = 'invoice_link',
  WalletAddress = 'wallet_address',
  Success = 'success',
  Failure = 'failure',
}

export interface PaymentState {
  payment: Array<Payment>,
  transactions: TransactionListState,
  currencies: Currency[],
  chargePaymentTypes: PaymentType[],
  withdrawPaymentTypes: PaymentType[],
  chargeForm: FormGroupState<PaymentChargeFormState>,
  withdrawForm: FormGroupState<PaymentWithdrawFormState>,
  isLoading: boolean
  paymentMode: PaymentModes
  paymentStatus: PaymentStatus | null
  walletAddress: string | null,
  walletAmount: number | null,
  invoiceId: string | null,
  invoice: InvoiceLinkApi | null
}

export const initialState: PaymentState = {
  payment: [],
  transactions: transactionListAdapter.getInitialState({
    currentView: UserTransactionsViewFilter.ALL,
    isLoading: false,
  }),
  currencies: [],
  chargePaymentTypes: [],
  withdrawPaymentTypes: [],
  chargeForm: initialPaymentChargeFormState,
  withdrawForm: initialPaymentWithdrawFormState,
  isLoading: false,
  paymentMode: PaymentModes.Default,
  paymentStatus: null,
  walletAddress: null,
  walletAmount: null,
  invoiceId: null,
  invoice: null,
};

export const paymentRowReducer = createReducer(
  initialState,
  onNgrxForms(),
  onNgrxFormsAction(SetValueAction, (state, { controlId, value } ) => {

    if (controlId === `${ PAYMENT_CHARGE_FORM_ID }.amount`) {
      const amount = <number>value;

      const selectedPaymentTypeValue = state.chargeForm.controls.paymentType.value;

      const selectedCurrencyValue = state.chargeForm.controls.currency.value;

      const selectedPaymentType = state.chargePaymentTypes
        .find(({ type }) => type === selectedPaymentTypeValue);

      const isPaymentTypeSupportAmount = !!selectedPaymentType
        && (amount >= selectedPaymentType.min  && amount <= selectedPaymentType.max);

      const firstPaymentTypeSupportsAmount = state.chargePaymentTypes
        .filter(({ iso_codes }) => iso_codes.find(code => code === selectedCurrencyValue))
        .filter(({ min, max }) => amount >= min  && amount <= max)[0];

      return {
        ...state,
        chargeForm: setValue(
          state.chargeForm,
          {
            ...state.chargeForm.value,
            paymentType: isPaymentTypeSupportAmount
              ? state.chargeForm.value.paymentType
              : firstPaymentTypeSupportsAmount?.type ?? null,
          },
        ),
      };

    }

    if (controlId === `${ PAYMENT_CHARGE_FORM_ID }.currency`) {

      const selectedCurrencyCode = value;

      const selectedPaymentTypeValue = state.chargeForm.controls.paymentType.value;

      const selectedPaymentType = state.chargePaymentTypes
        .find(({ type }) => type === selectedPaymentTypeValue);

      const isPaymentTypeSupportSelectedCurrency = selectedPaymentType
        ?.iso_codes
        .some(code => code === selectedCurrencyCode);

      const selectedCurrency = state.currencies
        .find(currency => currency.iso_code === selectedCurrencyCode);

      const firstDefaultAmountForCurrency = selectedCurrency?.default_sum[0] ?? 0;

      const firstPaymentTypeSupportsSelectedCurrency = state.chargePaymentTypes
        .filter(({ min }) => min <= firstDefaultAmountForCurrency)
        .find(({ name }) => name === 'Volet');

      return {
        ...state,
        chargeForm: setValue(
          state.chargeForm,
          {
            ...state.chargeForm.value,
            paymentType: !!isPaymentTypeSupportSelectedCurrency
              ? state.chargeForm.value.paymentType
              : firstPaymentTypeSupportsSelectedCurrency?.type ?? null,
            amount: selectedCurrency?.default_sum[0] ?? 0,
          },
        ),
      };
    }

    if (controlId === `${ PAYMENT_WITHDRAW_FORM_ID }.paymentType`) {
      const selectedPaymentType = state.withdrawPaymentTypes
        .find(paymentType => paymentType.type === value);

      let withdrawForm = markAsUntouched(
        markAsPristine(
          setValue(
            state.withdrawForm,
            {
              ...state.withdrawForm.value,
              address: null,
              currency: selectedPaymentType?.iso_codes[0] ?? null,
              amount: selectedPaymentType?.min ?? 0,
            },
          ),
        ),
      );

      return {
        ...state,
        withdrawForm,
      };
    }

    if (controlId === `${ PAYMENT_WITHDRAW_FORM_ID }.address`) {
      return {
        ...state,
        withdrawForm: {
          ...state.withdrawForm,
          controls: {
            ...state.withdrawForm.controls,
            address: markAsTouched(markAsDirty(state.withdrawForm.controls.address)),
          },
        },
      };
    }

    return state;
  }),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onNgrxFormsAction(StartAsyncValidationAction, (state, action) => {
    return state;
  }),

  on(PaymentActions.loadCurrencyList,
    (state): PaymentState => ({ ...state, isLoading: true }),
  ),
  on(PaymentActions.loadCurrencyListSuccess,
    (state, { currencies }): PaymentState => ({
      ...state, currencies, isLoading: false,
    }),
  ),
  on(PaymentActions.loadCurrencyListFailure,
    (state): PaymentState => ({ ...state, isLoading: false }),
  ),

  on(PaymentActions.loadPaymentTypeList,
    (state): PaymentState => ({
      ...state, isLoading: true,
    }),
  ),
  on(PaymentActions.loadPaymentTypeListSuccess,
    (state, { withdraw_types, charge_types }): PaymentState => ({
      ...state,
      chargePaymentTypes: charge_types,
      withdrawPaymentTypes: withdraw_types,
      isLoading: false,
    }),
  ),
  on(
    PaymentActions.clearChargeForm,
    (state): PaymentState => ({
      ...state,
      chargeForm: initialPaymentChargeFormState,
    }),
  ),
  on(
    PaymentActions.clearWithdrawForm,
    (state): PaymentState => ({
      ...state,
      withdrawForm: initialPaymentWithdrawFormState,
    }),
  ),
  on(PaymentActions.addPayments,
    (state): PaymentState => ({
      ...state,
    }),
  ),
  on(PaymentActions.addPaymentsSuccess,
    (state, payload: { payment: Payment }): PaymentState => ({
      ...state,
      payment: state.payment.concat(payload.payment),
    }),
  ),
  on(PaymentActions.addPaymentsFailure,
    (state): PaymentState => ({
      ...state,
    }),
  ),
  on(PaymentActions.loadPayments,
    (state): PaymentState => ({
      ...state, isLoading: true,
    }),
  ),
  on(PaymentActions.loadPaymentsSuccess,
    (state, payload: { payments: Array<Payment> }): PaymentState => ({
      ...state,
      payment: payload.payments, isLoading: false,
    }),
  ),
  on(PaymentActions.loadPaymentsFailure,
    (state): PaymentState => ({
      ...state,
    }),
  ),
  on(
    PaymentActions.loadTransactions,
    PaymentActions.loadTransactionsNextPage,
    (state): PaymentState => ({
      ...state,
      transactions: {
        ...state.transactions,
        isLoading: true,
      },
    }),
  ),
  on(PaymentActions.loadTransactionsSuccess,
    (state, { transactions, nextPage }): PaymentState => ({
      ...state,
      transactions: transactionListAdapter.setAll(transactions, {
        ...state.transactions,
        nextPage,
        isLoading: false,
      }),
    }),
  ),
  on(PaymentActions.loadTransactionsNextPageSuccess,
    (state, { transactions, nextPage }): PaymentState => ({
      ...state,
      transactions: transactionListAdapter.addMany(transactions, {
        ...state.transactions,
        nextPage,
        isLoading: false,
      }),
    }),
  ),
  on(PaymentActions.loadTransactionsFailure,
    (state): PaymentState => ({
      ...state,
      transactions: {
        ...state.transactions,
        isLoading: false,
      },
    }),
  ),
  on(PaymentActions.changeTransactionsViewFilter,
    (state, { currentView }): PaymentState => ({
      ...state,
      transactions: {
        ...state.transactions,
        currentView,
      },
    }),
  ),
  on(paymentActions.setPaymentMode,
    (state, { paymentMode }): PaymentState => ({
      ...state, paymentMode,
    }),
  ),

  on(PaymentActions.sendChargePaymentRequest,
    (state): PaymentState => ({ ...state }),
  ),
  on(PaymentActions.sendChargePaymentRequestSuccess,
    (state, { invoice }): PaymentState => ({
      ...state,
      paymentMode: invoice.field as PaymentModes,
      walletAddress: invoice.wallet_address ?? null,
      walletAmount: invoice.amount ?? null,
      invoiceId: invoice.payment_id ?? null,
      invoice,
    }),
  ),
);


export const paymentChargeRowReducer = wrapReducerWithFormStateUpdate(
  paymentRowReducer,
  (state) => state.chargeForm,
  validatePaymentChargeForm,
);

export const paymentReducer = wrapReducerWithFormStateUpdate(
  paymentChargeRowReducer,
  (state) => state.withdrawForm,
  validatePaymentWithdrawForm,
);
